import { z } from 'zod';
import { STAFF_ROLES, type StaffRole } from '../reference/roles';

export const dashboardTemplateRoleSchema = z.enum(STAFF_ROLES);
export type DashboardTemplateRole = z.infer<typeof dashboardTemplateRoleSchema>;

export const dashboardSectionKeySchema = z.enum(['myWork', 'myTeamWork', 'compliance', 'engagement', 'proposal', 'invoices', 'receivables', 'time', 'goals', 'attendance']);
export type DashboardSectionKey = z.infer<typeof dashboardSectionKeySchema>;
export const DASHBOARD_SECTION_ORDER: DashboardSectionKey[] = ['myWork', 'myTeamWork', 'compliance', 'engagement', 'proposal', 'invoices', 'receivables', 'time', 'goals', 'attendance'];
export const DASHBOARD_SECTION_LABELS: Record<DashboardSectionKey, string> = {
  myWork: 'My Work', myTeamWork: 'My Team Work', compliance: 'Compliance', engagement: 'Engagement', proposal: 'Proposal', invoices: 'Invoices', receivables: 'Receivables', time: 'Time', goals: 'Goals', attendance: 'Attendance',
};

export const dashboardWidgetKeySchema = z.enum([
  'myTasks', 'myAssignments', 'attention', 'upcoming', 'teamExceptions', 'teamWorkload', 'teamHours',
  'engagementPipeline', 'proposalPipeline', 'invoiceStatus', 'receivablesSummary', 'goalProgress', 'attendanceExceptions', 'meetings', 'myHours',
]);
export type DashboardWidgetKey = z.infer<typeof dashboardWidgetKeySchema>;

export interface DashboardWidgetDefinition {
  label: string;
  description: string;
  section: DashboardSectionKey;
  scope: 'own' | 'team' | 'firm';
  view: 'any' | 'full';
  defaultSize: 'half' | 'full';
  defaultListLimit: number;
  minListLimit: number;
  maxListLimit: number;
}

export const DASHBOARD_WIDGET_REGISTRY: Record<DashboardWidgetKey, DashboardWidgetDefinition> = {
  myTasks: { label: 'My Tasks', description: 'One prioritised personal work queue', section: 'myWork', scope: 'own', view: 'any', defaultSize: 'full', defaultListLimit: 6, minListLimit: 3, maxListLimit: 12 },
  myAssignments: { label: 'My Assignments', description: 'Personal engagements, checklist tasks and compliance', section: 'myWork', scope: 'own', view: 'full', defaultSize: 'full', defaultListLimit: 6, minListLimit: 3, maxListLimit: 12 },
  attention: { label: 'Needs Attention', description: 'Overdue, due today and unscheduled work', section: 'compliance', scope: 'firm', view: 'any', defaultSize: 'full', defaultListLimit: 4, minListLimit: 2, maxListLimit: 8 },
  upcoming: { label: 'Upcoming Deadlines', description: 'This week or later this month', section: 'compliance', scope: 'firm', view: 'any', defaultSize: 'full', defaultListLimit: 6, minListLimit: 3, maxListLimit: 8 },
  teamExceptions: { label: 'Team Work', description: 'Team overdue work and high workload', section: 'myTeamWork', scope: 'team', view: 'full', defaultSize: 'full', defaultListLimit: 8, minListLimit: 3, maxListLimit: 15 },
  teamWorkload: { label: 'Team Workload', description: 'Open and overdue work by person', section: 'myTeamWork', scope: 'team', view: 'full', defaultSize: 'full', defaultListLimit: 10, minListLimit: 5, maxListLimit: 25 },
  teamHours: { label: 'Team Hours', description: 'Team hours today and this week', section: 'time', scope: 'team', view: 'full', defaultSize: 'full', defaultListLimit: 10, minListLimit: 5, maxListLimit: 25 },
  engagementPipeline: { label: 'Engagement Pipeline', description: 'Engagements by stage and checklist progress', section: 'engagement', scope: 'firm', view: 'full', defaultSize: 'full', defaultListLimit: 4, minListLimit: 2, maxListLimit: 8 },
  proposalPipeline: { label: 'Proposal Pipeline', description: 'Draft, sent and accepted proposals', section: 'proposal', scope: 'firm', view: 'full', defaultSize: 'full', defaultListLimit: 4, minListLimit: 2, maxListLimit: 8 },
  invoiceStatus: { label: 'Invoice Status', description: 'Draft, issued, overdue and paid invoices', section: 'invoices', scope: 'firm', view: 'full', defaultSize: 'full', defaultListLimit: 6, minListLimit: 3, maxListLimit: 12 },
  receivablesSummary: { label: 'Receivables', description: 'Outstanding balances and aging by currency', section: 'receivables', scope: 'firm', view: 'full', defaultSize: 'full', defaultListLimit: 6, minListLimit: 3, maxListLimit: 12 },
  goalProgress: { label: 'Goals', description: 'Current progress against active goals', section: 'goals', scope: 'firm', view: 'full', defaultSize: 'full', defaultListLimit: 6, minListLimit: 3, maxListLimit: 12 },
  attendanceExceptions: { label: 'Attendance Exceptions', description: 'Missing checkout and pending leave approvals', section: 'attendance', scope: 'team', view: 'full', defaultSize: 'full', defaultListLimit: 8, minListLimit: 3, maxListLimit: 15 },
  meetings: { label: "Today's Meetings", description: 'Meetings organised by or assigned to the user', section: 'myWork', scope: 'own', view: 'any', defaultSize: 'full', defaultListLimit: 6, minListLimit: 3, maxListLimit: 12 },
  myHours: { label: 'My Hours', description: 'Personal hours today and this week', section: 'time', scope: 'own', view: 'any', defaultSize: 'half', defaultListLimit: 2, minListLimit: 2, maxListLimit: 2 },
};

export const dashboardTemplateWidgetSchema = z.object({
  key: dashboardWidgetKeySchema,
  visible: z.boolean().default(true),
  required: z.boolean().default(false),
  size: z.enum(['half', 'full']),
  listLimit: z.number().int().min(2).max(25),
});
export type DashboardTemplateWidget = z.infer<typeof dashboardTemplateWidgetSchema>;

export const dashboardTemplateInputSchema = z.object({
  defaultSection: dashboardSectionKeySchema.optional(),
  widgets: z.array(dashboardTemplateWidgetSchema).max(20),
});
export type DashboardTemplateInput = z.infer<typeof dashboardTemplateInputSchema>;

export const dashboardLayoutSchema = z.object({
  role: dashboardTemplateRoleSchema,
  version: z.number().int().nonnegative(),
  source: z.enum(['default', 'published']),
  defaultSection: dashboardSectionKeySchema,
  widgets: z.array(dashboardTemplateWidgetSchema),
});
export type DashboardLayout = z.infer<typeof dashboardLayoutSchema>;

export const dashboardUserPreferenceSchema = z.object({
  hidden: z.array(dashboardWidgetKeySchema).max(20),
  order: z.array(dashboardWidgetKeySchema).max(20),
  listLimits: z.record(dashboardWidgetKeySchema, z.number().int().min(2).max(25)),
});
export type DashboardUserPreference = z.infer<typeof dashboardUserPreferenceSchema>;

function widget(key: DashboardWidgetKey, visible = true, required = false): DashboardTemplateWidget {
  const definition = DASHBOARD_WIDGET_REGISTRY[key];
  return { key, visible, required, size: definition.defaultSize, listLimit: definition.defaultListLimit };
}

const DEFAULTS: Record<StaffRole, DashboardTemplateWidget[]> = {
  CEO: [widget('teamExceptions'), widget('engagementPipeline'), widget('proposalPipeline'), widget('attention'), widget('upcoming'), widget('receivablesSummary'), widget('goalProgress'), widget('attendanceExceptions'), widget('meetings'), widget('teamWorkload'), widget('teamHours'), widget('invoiceStatus')],
  GM: [widget('teamExceptions'), widget('engagementPipeline'), widget('proposalPipeline'), widget('attention'), widget('upcoming'), widget('receivablesSummary'), widget('goalProgress'), widget('attendanceExceptions'), widget('meetings'), widget('teamWorkload'), widget('teamHours'), widget('invoiceStatus')],
  Manager: [widget('teamExceptions'), widget('upcoming'), widget('teamWorkload'), widget('teamHours'), widget('engagementPipeline'), widget('attendanceExceptions'), widget('meetings'), widget('myAssignments', false), widget('goalProgress'), widget('proposalPipeline', false), widget('invoiceStatus', false), widget('receivablesSummary', false)],
  TeamLead: [widget('teamExceptions'), widget('myTasks', true, true), widget('upcoming'), widget('teamWorkload'), widget('teamHours'), widget('engagementPipeline'), widget('attendanceExceptions'), widget('meetings'), widget('proposalPipeline', false), widget('invoiceStatus', false), widget('receivablesSummary', false), widget('goalProgress', false)],
  Staff: [widget('myTasks', true, true), widget('attention'), widget('upcoming'), widget('meetings'), widget('myHours')],
};

export function defaultDashboardWidgets(role: StaffRole): DashboardTemplateWidget[] {
  return DEFAULTS[role].map((entry) => ({ ...entry }));
}

export function defaultDashboardSection(role: StaffRole): DashboardSectionKey {
  return role === 'Staff' ? 'myWork' : 'myTeamWork';
}

export function validateDashboardTemplate(role: StaffRole, widgets: DashboardTemplateWidget[]): void {
  const seen = new Set<DashboardWidgetKey>();
  for (const entry of widgets) {
    if (seen.has(entry.key)) throw new Error(`Duplicate widget: ${entry.key}`);
    seen.add(entry.key);
    const definition = DASHBOARD_WIDGET_REGISTRY[entry.key];
    if (!definition) throw new Error(`Unknown widget: ${entry.key}`);
    if (definition.view === 'full' && role === 'Staff') throw new Error(`${definition.label} is not available for Staff`);
    if (entry.required && !entry.visible) throw new Error(`${definition.label} is required and cannot be hidden`);
    if (entry.listLimit < definition.minListLimit || entry.listLimit > definition.maxListLimit) {
      throw new Error(`${definition.label} list limit must be ${definition.minListLimit}–${definition.maxListLimit}`);
    }
  }
}
